import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PartialObserver, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import * as _ from 'lodash';

import {
    AgentsService,
    AuthService,
    PermissionsService,
    ToastService,
} from '@core/services';
import {
    IAgentPermissions,
    IAgentPermissionsForm,
} from '@core/interfaces';
import { environment } from '@env/environment';
import { AdminUsersService } from '@pages/admin-users';


@Component({
    selector: 'app-permission-management',
    templateUrl: './permission-management.component.html',
    styleUrls: ['./permission-management.component.scss']
})
export class PermissionManagementComponent implements OnInit {
    public isAdmin: boolean = environment.role === 'admin';
    public isDesktopView: boolean = window.innerWidth > 620;
    public isEditMode: boolean = false;
    public agentId: number;
    public agentPermissions: IAgentPermissions;
    public agentPermissionsOldValue: IAgentPermissions;
    public permissionManagementForm: FormGroup = new FormGroup<Partial<IAgentPermissionsForm>>({
        Id: new FormControl(null),
        AgentAdd: new FormControl(false),
        AgentBlock: new FormControl(false),
        AgentChangePassword: new FormControl(false),
        DepositToAgent: new FormControl(false),
        DepositToPlayer: new FormControl(false),
        PlayerAdd: new FormControl(false),
        PlayerBlock: new FormControl(false),
        PlayerChangePassword: new FormControl(false),
        WithdrawalFromAgent: new FormControl(false),
        WithdrawalFromPlayer: new FormControl(false),
        AgentIndirectTransfer: new FormControl(false),
        PlayerIndirectTransfer: new FormControl(false),
    });
    private isPermissionManagementSame: boolean = false;

    public pending: boolean = false;

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        private agentsService: AgentsService,
        private route: ActivatedRoute,
        private dialogService: DialogService,
        private translateService: TranslateService,
        private toastService: ToastService,
        private authService: AuthService,
        private adminUsersService: AdminUsersService,
        public permissionsService: PermissionsService,
    ) {
    }

    ngOnInit(): void {
        this.getTemplatesByAgentId();
        this.followFormValueChanges();
    }

    private getTemplatesByAgentId(): void {
        this.agentId = this.route.parent.snapshot.params.id;
        this.agentsService.getPermissionsByAgentId(this.agentId ? this.agentId : this.authService.userData$.getValue().Id, this.isAdmin)
            .pipe(take(1))
            .subscribe(res => {
                this.agentPermissions = res;
            }, err => {
                this.toastService.showToastMsg('error', err?.error?.message);
            });
    }

    public enableEditMode(): void {
        this.isEditMode = true;
        this.permissionManagementForm.reset();
        this.permissionManagementForm.patchValue(this.agentPermissions);
    }

    public onCancel(): void {
        this.isEditMode = false;
    }

    public onSaveChanges(): void {
        if (this.permissionManagementForm.valid) {
            if (!this.isPermissionManagementSame) {
                this.editAgentPermissions();
            } else {
                this.isEditMode = false;
            }
        } else {
            this.permissionManagementForm.markAllAsTouched();
        }
    }

    private editAgentPermissions(): void {
        const payload: IAgentPermissions = {...this.agentPermissions, ...this.permissionManagementForm.value};

        this.pending = true;

        if (this.isAdmin) {
            this.adminUsersService.editAgentPermissions(payload)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(this.insertEditAgentPermissionsObserver());
        } else {
            this.agentsService.editAgentPermissions(payload)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(this.insertEditAgentPermissionsObserver());
        }
    }

    private insertEditAgentPermissionsObserver(): PartialObserver<IAgentPermissions> {
        return {
            next: () => {
                this.pending = false;
                this.agentPermissions = { ...this.agentPermissions, ...this.permissionManagementForm.value };
                this.agentPermissionsOldValue = this.permissionManagementForm.value;

                this.isEditMode = false;
                this.toastService.showToastMsg('success', 'UpdateSuccessMessage', 'AgentPermissionList');
            },
            error: (err: HttpErrorResponse) => {
                this.pending = false;
                this.toastService.showToastMsg('error', err?.error?.message);
            }
        };
    }


    private followFormValueChanges(): void {
        this.permissionManagementForm.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(res => {
                if (res && this.permissionManagementForm.value) {
                    if (!this.agentPermissionsOldValue && this.permissionManagementForm.controls.Id.value) {
                        this.agentPermissionsOldValue = { ...this.permissionManagementForm.value };
                    }
                    this.isPermissionManagementSame = _.isEqual(this.agentPermissionsOldValue, res);
                }
            });
    }
}
